import React, { useId, useMemo, useRef } from 'react';
import SpriteSVG from '@components/1-atoms/SpriteSVG';
import DropDownMenuPortal from '@components/2-molecules/DropDownMenuPortal';
import useDropDownMenu from '@hooks/useDropDownMenu';
import {
  FieldGroup,
  FieldLabel,
  InlineError,
  RequiredMark,
  SelectTrigger,
  SelectTriggerIcon,
  SelectTriggerLabel,
} from '../Onboarding.styled';
import type { Option } from '../options';

export interface LabeledSelectProps {
  id: string;
  label: string;
  placeholder: string;
  options: ReadonlyArray<Option>;
  value: string;
  onChange: (next: string) => void;
  error?: string;
  dataTestId?: string;
  /** When true, renders a red asterisk after the label. */
  required?: boolean;
}

/**
 * Compact onboarding dropdown. Wraps the existing `useDropDownMenu` /
 * `DropDownMenuPortal` plumbing so we get the design system's menu surface,
 * keyboard handling, click-outside, and theming — without inheriting the
 * heavyweight `Field` chrome that `DropDownField` brings (60px+ tall, big
 * padding). The trigger mirrors `TextInput`/`SelectTrigger`'s 44px height
 * for a clean visual rhythm with the rest of the form.
 */
const LabeledSelect: React.FC<LabeledSelectProps> = ({
  id,
  label,
  placeholder,
  options,
  value,
  onChange,
  error,
  dataTestId,
  required,
}) => {
  const testId = dataTestId ?? `onboarding-select-${id}`;
  // useId gives us a process-stable id even with React strict mode double
  // mounts, which keeps `useDropDownMenu`'s open-state in sync.
  const reactId = useId();
  const menuId = `onboarding-select-${reactId}`;
  const triggerRef = useRef<HTMLButtonElement>(null);

  const { showDropDownMenu, dropDownMenuId, dropDownMenuIsOpen } =
    useDropDownMenu();
  const open = dropDownMenuIsOpen && dropDownMenuId === menuId;

  const selectedOption = useMemo(
    () => options.find((opt) => opt.id === value),
    [options, value],
  );

  const menuItems = useMemo(
    () =>
      options.map((opt) => ({
        id: opt.id,
        label: opt.label,
        selected: opt.id === value,
        // `formFieldValue.value` is what comes back via `onFormSelect`.
        formFieldValue: { label: opt.label, value: opt.id },
      })),
    [options, value],
  );

  const handleClick = () => {
    if (!triggerRef.current) return;
    showDropDownMenu(menuId, triggerRef.current, {
      fullWidth: true,
      position: 'bottom-left',
    });
  };

  const handleSelect = (formFieldValue: { value: string }) => {
    onChange(formFieldValue.value);
  };

  return (
    <FieldGroup>
      <FieldLabel htmlFor={id}>
        {label}
        {required && <RequiredMark aria-hidden="true">*</RequiredMark>}
      </FieldLabel>
      <SelectTrigger
        id={id}
        ref={triggerRef}
        type="button"
        data-testid={testId}
        $open={open}
        $empty={!selectedOption}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={handleClick}
      >
        <SelectTriggerLabel>
          {selectedOption?.label ?? placeholder}
        </SelectTriggerLabel>
        <SelectTriggerIcon $open={open} aria-hidden>
          <SpriteSVG name="expand_more_0" />
        </SelectTriggerIcon>
      </SelectTrigger>
      {error && (
        <InlineError data-testid={`${testId}__error`} role="alert">
          {error}
        </InlineError>
      )}
      <DropDownMenuPortal
        menuId={menuId}
        menuItems={menuItems}
        onFormSelect={handleSelect}
      />
    </FieldGroup>
  );
};

export default LabeledSelect;
